import type { Character } from '../types/game'

/**
 * Generate avatar URL using DiceBear API
 * DiceBear provides free, open-source avatar generation with multiple styles
 * Docs: https://www.dicebear.com/
 */
export function generateAvatarUrl(character: Character): string {
  const { name, attributes } = character

  // Use 'avataaars' style which supports detailed customization
  const baseUrl = 'https://api.dicebear.com/7.x/avataaars/svg'

  // Build query parameters based on character attributes
  const params = new URLSearchParams()

  // Seed ensures same character always gets same avatar
  params.append('seed', name)

  // Set accessories based on attributes
  if (attributes.hasGlasses) {
    params.append('glasses', 'round,prescription01,prescription02')
    params.append('glassesProbability', '100')
  } else {
    params.append('glassesProbability', '0')
  }

  if (attributes.hasHat) {
    params.append('hat', 'beanie,turban,winter01,winter02')
    params.append('hatProbability', '100')
  } else {
    params.append('hatProbability', '0')
  }

  // Hair color mapping
  const hairColorMap: Record<string, string> = {
    'black': '262e33',
    'brown': '4a312c,724133',
    'blonde': 'd6b370,f59797',
    'red': 'c93305,e16381',
    'gray': 'b1b1b1,e8e1e1',
  }

  if (attributes.hairColor && hairColorMap[attributes.hairColor.toLowerCase()]) {
    params.append('hairColor', hairColorMap[attributes.hairColor.toLowerCase()])
  }

  // Facial hair for males
  if (attributes.facialHair && attributes.gender === 'male') {
    params.append('facialHair', 'beardMedium,beardLight,moustacheFancy,moustacheMagnum')
    params.append('facialHairProbability', '100')
  } else {
    params.append('facialHairProbability', '0')
  }

  // Hair style mapping
  const hairStyleMap: Record<string, string> = {
    'bald': 'noHair',
    'short': 'shortFlat,shortRound,shortWaved',
    'medium': 'dreads01,shavedSides,straight01',
    'long': 'long,dreads02,frizzle',
    'curly': 'frizzle,dreads01',
  }

  if (attributes.hairStyle && hairStyleMap[attributes.hairStyle.toLowerCase()]) {
    params.append('hair', hairStyleMap[attributes.hairStyle.toLowerCase()])
  }

  return `${baseUrl}?${params.toString()}`
}

/**
 * Alternative: Generate avatar using Boring Avatars (fully local, no API)
 */
export function generateLocalAvatar(name: string, size: number = 120): string {
  // This would use a React component instead
  return `https://source.boringavatars.com/beam/${size}/${encodeURIComponent(name)}?colors=667eea,764ba2,f093fb,4facfe,43e97b`
}

/**
 * Fallback: Generate simple SVG avatar with initials
 */
export function generateFallbackAvatar(name: string, color: string = '#667eea'): string {
  const initials = name.substring(0, 2).toUpperCase()
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="50" fill="${color}"/>
      <text
        x="50"
        y="50"
        text-anchor="middle"
        dy=".35em"
        font-family="Arial, sans-serif"
        font-size="40"
        font-weight="bold"
        fill="white"
      >${initials}</text>
    </svg>
  `
  return `data:image/svg+xml;base64,${btoa(svg)}`
}
